import React, { useState, useEffect } from 'react';
import axios from 'axios';
import GeofenceForm from './GeofenceForm';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Geofence Management Component
 * Shows list of existing geofences with options to activate/deactivate, edit, delete, or create new
 */
const GeofenceManagement = ({ vehicle, onClose, currentUser }) => {
  const [geofences, setGeofences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingGeofence, setEditingGeofence] = useState(null);
  const [deletingGeofence, setDeletingGeofence] = useState(null);
  const [toast, setToast] = useState(null);

  // Fetch geofences for this vehicle
  useEffect(() => {
    fetchGeofences();
  }, [vehicle.id]);

  const fetchGeofences = async () => {
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');

      if (!token) {
        setError('Not authenticated. Please log in again.');
        setLoading(false);
        return;
      }

      const response = await axios.get(
        `${API}/geofences?vehicle_id=${vehicle.id}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      setGeofences(response.data);
    } catch (err) {
      // Display more detailed error message
      const errorMessage = err.response?.data?.detail || err.response?.data?.message || 'Failed to load geofences. Please try again.';
      setError(errorMessage);

      // If 401 error, suggest re-login
      if (err.response?.status === 401) {
        setError('Session expired. Please log out and log in again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (geofence) => {
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      await axios.put(
        `${API}/geofences/${geofence.id}`,
        { is_active: !geofence.is_active },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      // Update local state
      setGeofences(geofences.map(g =>
        g.id === geofence.id ? { ...g, is_active: !g.is_active } : g
      ));

      setToast({
        type: 'success',
        message: `Geofence ${!geofence.is_active ? 'activated' : 'deactivated'}`
      });
    } catch (err) {
      setToast({
        type: 'error',
        message: 'Failed to update geofence'
      });
    }
  };

  const handleDelete = async (geofenceId) => {
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      await axios.delete(
        `${API}/geofences/${geofenceId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      // Update local state
      setGeofences(geofences.filter(g => g.id !== geofenceId));
      setDeletingGeofence(null);

      setToast({
        type: 'success',
        message: 'Geofence deleted successfully'
      });
    } catch (err) {
      setToast({
        type: 'error',
        message: 'Failed to delete geofence'
      });
    }
  };

  const handleCreateSuccess = () => {
    setShowCreateForm(false);
    fetchGeofences(); // Refresh the list
    setToast({
      type: 'success',
      message: 'Geofence created successfully'
    });
  };

  const handleEditSuccess = () => {
    setEditingGeofence(null);
    fetchGeofences(); // Refresh the list
    setToast({
      type: 'success',
      message: 'Geofence updated successfully'
    });
  };

  // Auto-hide toast after 3 seconds
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  // If showing create form
  if (showCreateForm) {
    return (
      <GeofenceForm
        vehicle={vehicle}
        currentUser={currentUser}
        onClose={() => setShowCreateForm(false)}
        onSuccess={handleCreateSuccess}
      />
    );
  }

  // If editing a geofence
  if (editingGeofence) {
    return (
      <GeofenceForm
        vehicle={vehicle}
        currentUser={currentUser}
        currentGeofence={editingGeofence}
        onClose={() => setEditingGeofence(null)}
        onSuccess={handleEditSuccess}
        isEditing={true}
      />
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-xl font-bold text-gray-800">
              Geofences - {vehicle.year} {vehicle.make} {vehicle.model}
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              Manage virtual boundaries for this vehicle
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl"
          >
            ×
          </button>
        </div>

        {/* Toast Notification */}
        {toast && (
          <div className={`mb-4 px-4 py-3 rounded ${
            toast.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
          }`}>
            {toast.message}
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {/* Loading State */}
        {loading ? (
          <div className="text-center py-12">
            <div className="text-gray-600">Loading geofences...</div>
          </div>
        ) : (
          <>
            {/* Create New Button */}
            <button
              onClick={() => setShowCreateForm(true)}
              className="w-full bg-blue-600 text-white px-4 py-3 rounded-md hover:bg-blue-700 transition-colors mb-4 font-medium"
            >
              + Create New Geofence
            </button>

            {/* Geofences List */}
            {geofences.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-lg">
                <div className="text-6xl mb-4">🗺️</div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No geofences yet</h3>
                <p className="text-gray-600 mb-4">
                  Create your first geofence to get notified when your vehicle enters or exits specific areas
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                <h4 className="font-medium text-gray-700 mb-2">
                  Existing Geofences ({geofences.length})
                </h4>
                {geofences.map((geofence) => (
                  <div
                    key={geofence.id}
                    className={`border rounded-lg p-4 transition-all ${
                      geofence.is_active
                        ? 'border-blue-200 bg-blue-50'
                        : 'border-gray-200 bg-gray-50'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      {/* Geofence Info */}
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <h5 className="font-semibold text-gray-900">{geofence.name}</h5>
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            geofence.is_active
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-200 text-gray-600'
                          }`}>
                            {geofence.is_active ? 'Active' : 'Inactive'}
                          </span>
                          <span className="px-2 py-1 text-xs rounded-full bg-purple-100 text-purple-800">
                            {geofence.area.type}
                          </span>
                        </div>

                        {geofence.description && (
                          <p className="text-sm text-gray-600 mb-2">{geofence.description}</p>
                        )}

                        <div className="text-xs text-gray-500 space-y-1">
                          <div>📧 {geofence.notification_email}</div>
                          <div className="flex items-center space-x-3">
                            <span>{geofence.notify_on_enter ? '✅ Enter alerts' : '❌ No enter alerts'}</span>
                            <span>{geofence.notify_on_exit ? '✅ Exit alerts' : '❌ No exit alerts'}</span>
                          </div>
                          {geofence.area.type === 'Circle' && (
                            <div>📏 Radius: {geofence.area.radius}m</div>
                          )}
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex flex-col space-y-2 ml-4">
                        {/* Toggle Active/Inactive */}
                        <button
                          onClick={() => handleToggleActive(geofence)}
                          className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                            geofence.is_active
                              ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                              : 'bg-green-100 text-green-700 hover:bg-green-200'
                          }`}
                        >
                          {geofence.is_active ? 'Deactivate' : 'Activate'}
                        </button>

                        {/* Edit Button */}
                        <button
                          onClick={() => setEditingGeofence(geofence)}
                          className="px-3 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium hover:bg-blue-200 transition-colors"
                        >
                          Edit
                        </button>

                        {/* Delete Button */}
                        <button
                          onClick={() => setDeletingGeofence(geofence.id)}
                          className="px-3 py-1 bg-red-100 text-red-700 rounded text-xs font-medium hover:bg-red-200 transition-colors"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Close Button */}
        <div className="mt-6">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deletingGeofence && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Delete Geofence?</h3>
            <p className="text-gray-600 mb-4">
              Are you sure you want to delete this geofence? This action cannot be undone.
            </p>
            <div className="flex space-x-3">
              <button
                onClick={() => setDeletingGeofence(null)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deletingGeofence)}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GeofenceManagement;
